import { apiRequest } from "./api";
import type {
  TokenPairResponse,
  EnvelopeResponse,
  User,
  UserCreateRequest,
  UserUpdateRequest,
  ActivateUserRequest,
  AssignPermissionsRequest,
  PaginatedResponse,
  Group,
  WhatsAppRole,
  AssignWhatsAppRoleRequest,
} from "../types/auth";

export async function login(username: string, password: string): Promise<TokenPairResponse> {
  return apiRequest<TokenPairResponse>("/api/v1/token/", {
    method: "POST",
    body: { username, password },
  });
}

export async function registerUser(data: {
  username: string;
  email: string;
  password: string;
  identificacion?: string;
}): Promise<EnvelopeResponse<unknown>> {
  return apiRequest<EnvelopeResponse<unknown>>("/api/v1/registrar-usuario/", {
    method: "POST",
    body: data,
  });
}

// ========== USER MANAGEMENT ==========

export async function listUsers(params?: {
  page?: number;
  page_size?: number;
  search?: string;
  is_staff?: boolean;
}): Promise<PaginatedResponse<User>> {
  return apiRequest<PaginatedResponse<User>>("/api/v1/usuarios/", {
    params: params as Record<string, string | number | boolean | undefined>,
  });
}

export async function createUser(data: UserCreateRequest): Promise<EnvelopeResponse<User>> {
  return apiRequest<EnvelopeResponse<User>>("/api/v1/crear-usuario/", {
    method: "POST",
    body: data,
  });
}

export async function updateUser(id: number, data: UserUpdateRequest): Promise<EnvelopeResponse<User>> {
  return apiRequest<EnvelopeResponse<User>>(`/api/v1/actualizar-usuario/${id}/`, {
    method: "PUT",
    body: data,
  });
}

export async function activateUser(id: number, data: ActivateUserRequest): Promise<EnvelopeResponse<User>> {
  return apiRequest<EnvelopeResponse<User>>(`/api/v1/activar-usuario/${id}/`, {
    method: "PUT",
    body: data,
  });
}

export async function assignPermissions(data: AssignPermissionsRequest): Promise<EnvelopeResponse<unknown>> {
  return apiRequest<EnvelopeResponse<unknown>>("/api/v1/asignar-permisos/", {
    method: "POST",
    body: data,
  });
}

// ========== GROUPS ==========

export async function listGroups(params?: {
  page?: number;
  page_size?: number;
  search?: string;
}): Promise<PaginatedResponse<Group>> {
  return apiRequest<PaginatedResponse<Group>>("/api/v1/grupos/", {
    params: params as Record<string, string | number | boolean | undefined>,
  });
}

export async function createGroup(data: { name: string; permissions?: number[] }): Promise<EnvelopeResponse<Group>> {
  return apiRequest<EnvelopeResponse<Group>>("/api/v1/crear-grupo/", {
    method: "POST",
    body: data,
  });
}

export async function updateGroup(id: number, data: { name?: string; permissions?: number[] }): Promise<EnvelopeResponse<Group>> {
  return apiRequest<EnvelopeResponse<Group>>(`/api/v1/actualizar-grupo/${id}/`, {
    method: "PUT",
    body: data,
  });
}

// ========== WHATSAPP ROLES ==========

export async function assignWhatsAppRole(data: AssignWhatsAppRoleRequest): Promise<EnvelopeResponse<unknown>> {
  return apiRequest<EnvelopeResponse<unknown>>("/api/v1/whatsapp-role/", {
    method: "POST",
    body: data,
  });
}

export async function listUserWhatsAppRoles(userId: number): Promise<WhatsAppRole[]> {
  const response = await apiRequest<WhatsAppRole[]>(`/api/v1/whatsapp-role/list/?user_id=${userId}`);
  return Array.isArray(response) ? response : [];
}

// ========== JWT HELPERS ==========

export interface DecodedToken {
  user_id: number;
  username: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
  exp: number;
}

export function decodeToken(token: string): DecodedToken | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return {
      user_id: decoded.user_id,
      username: decoded.username || "",
      email: decoded.email || "",
      is_staff: decoded.is_staff || false,
      is_superuser: decoded.is_superuser || false,
      exp: decoded.exp || 0,
    };
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded) return true;
  return Date.now() >= decoded.exp * 1000;
}